import { type ApolloError, useQuery } from "@apollo/client";
import { useMemo } from "react";

import type { GetClosedWinsQuery } from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../../lib/apollo/utils";
import { useAuth } from "../../../lib/auth";
import { getClosedWinsQuery } from "./queries";
import {
  type ClosedCaseLike,
  type ClosedWin,
  selectPrimaryWin,
  toClosedWin,
} from "./resolveClosedWin";

type ClosedEvent = NonNullable<
  GetClosedWinsQuery["issue__ClosedEvent"]
>[number];

// Opening the case screen stamps Issue.lastSeenAt, so "the user has seen the
// closure" is lastSeenAt catching up to the ClosedEvent's sentAt.
const isClosureSeen = (event: ClosedEvent): boolean =>
  event.issue.lastSeenAt != null &&
  event.sentAt != null &&
  Date.parse(event.issue.lastSeenAt) >= Date.parse(event.sentAt);

// ClosedEvent only surfaces cases without feedback (reviewed → already gone), so
// every fetched row is a not-yet-reviewed candidate.
const toClosedCase = (event: ClosedEvent): ClosedCaseLike => ({
  id: event.issue.id,
  title: event.issue.title,
  caseType: event.issue.caseType,
  caseManagementClosureReason: event.issue.caseManagementClosureReason,
  userClosureReason: event.userClosureReason,
  closedAt: event.issue.closedAt,
  dollarAmountSaved: event.issue.dollarAmountSaved,
  providerName: event.issue.providerName,
  hasFeedback: false,
  closureSeen: isClosureSeen(event),
});

export type UseClosedWinResult = {
  /** The win for the CTA primary slot, if any (most-recent). */
  primaryWin: ClosedWin | null;
  /** Superseded wins (older than the primary) demoted into "Your cases". */
  demotedWins: ClosedWin[];
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<unknown>;
};

export function useClosedWin(): UseClosedWinResult {
  const { isAuthenticated, isReady, user } = useAuth();
  const userId = user?.id;

  const { data, loading, error, refetch } = useQuery(getClosedWinsQuery, {
    variables: { userId: userId ?? "" },
    context: errorHandling("caller"),
    skip: !isAuthenticated || !isReady || !userId,
  });

  return useMemo(() => {
    // A reopened → re-closed issue has multiple ClosedEvents; keep only the
    // newest per issue (events arrive sentAt DESC) so it can't render twice.
    const newestEventPerIssue = new Map<string, ClosedEvent>();
    for (const event of data?.issue__ClosedEvent ?? []) {
      if (!newestEventPerIssue.has(event.issue.id)) {
        newestEventPerIssue.set(event.issue.id, event);
      }
    }
    const cases = [...newestEventPerIssue.values()].map(toClosedCase);
    const primaryWin = selectPrimaryWin(cases);
    // Demoted = wins other than the one in the primary slot (older wins
    // superseded by a newer one). Reviewed cases already dropped out of the
    // query (feedback exists), so they never appear.
    const demotedWins = cases
      .map(toClosedWin)
      .filter((win): win is ClosedWin => win !== null)
      .filter(win => win.issueId !== primaryWin?.issueId);

    return { primaryWin, demotedWins, loading, error, refetch };
  }, [data, loading, error, refetch]);
}
